import React from "react";
import { Button } from "../../../components/ui";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import ProfileAvatar from "./ProfileAvatar";
import { addressLabel, experienceLabel, fullName } from "../profileFormat";
import type { DietitianProfile } from "../types/settings.types";

/** A field with nothing in it says so, rather than leaving a gap. */
const UNSET = "-";

/** Label followed by its value, the repeating unit of every card here. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-2">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}
    </span>
    <span className="min-w-0 text-[14px] 3xl:text-[15px] font-medium text-ink">
      {value}
    </span>
  </div>
);

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number — and it is rendered rather than stored,
 * the same way the form draws it beside the field.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="rounded-md border border-line bg-white">{children}</section>
);

interface ProfileOverviewProps {
  profile: DietitianProfile;
  /** Opens the form, filled with these same answers. */
  onEdit: () => void;
}

/**
 * The General tab at rest: who the dietitian is, how to reach them, and what
 * they work on — the three cards the Settings screen opens with.
 *
 * Read-only, and the one control on it is the way into the form. Which is the
 * point of the pair: the tab opens on what is saved rather than on a screenful
 * of inputs, so the commonest visit — coming to check a number — needs nothing
 * to be edited, and nothing can be changed by a stray keystroke on the way past.
 */
const ProfileOverview: React.FC<ProfileOverviewProps> = ({
  profile,
  onEdit,
}) => {
  const { fullName: accountName } = useSignedInUser();
  const experience = experienceLabel(profile);
  const address = addressLabel(profile);

  // Before the profile has a name on it, the account they signed in with is
  // still their name — the same one the rail greets them by. "Your profile" is
  // only what stands there for the moment before Clerk has loaded.
  const heading = fullName(profile) || accountName || "Your profile";

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3 px-5 sm:px-6 py-5">
          <ProfileAvatar photo={profile.photo} name={profile.firstName} />
          <h2 className="text-[18px] sm:text-[20px] font-medium text-ink">
            {heading}
          </h2>
          <span className="inline-flex rounded-full border border-[#A6EDF5] bg-[#F8FEFF] px-2.5 py-0.5 text-[12px] font-medium text-[#00899B]">
            {profile.active ? "Active" : "In-active"}
          </span>

          {/* Pushed to the end of the row so it reads as an action on the
              header rather than another badge — the same place the client and
              enquiry screens put their own. */}
          <Button size="sm" className="ml-auto" onClick={onEdit}>
            Edit Profile
          </Button>
        </div>

        {/* Spread edge to edge so the last field lines up with the card's
            right padding, the way the design has it. */}
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 border-t border-line px-5 sm:px-6 py-4">
          <Field label="Gender" value={profile.gender || UNSET} />
          <Field label="Age" value={profile.age || UNSET} />
          <Field label="Experience" value={experience || UNSET} />
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap gap-x-10 gap-y-3 px-5 sm:px-6 py-4">
          <Field
            label="Phone:"
            value={
              profile.phone ? (
                <DiallingCode>{profile.phone}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
          <Field
            label="WhatsApp"
            value={
              profile.whatsapp ? (
                <DiallingCode>{profile.whatsapp}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
        </div>
        <div className="border-t border-line px-5 sm:px-6 py-4">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Address
          </div>
          <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
            {address || UNSET}
          </div>
        </div>
      </Card>

      <Card>
        <div className="px-5 sm:px-6 py-4">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Specializations
          </div>
          {profile.specializations.length > 0 ? (
            // Wrapped rather than fitted to one line: this card is the last on
            // the screen and has the room, and a specialization behind a "+2"
            // is the one thing on the page a reader came to check.
            <ul className="mt-3 flex flex-wrap gap-2">
              {profile.specializations.map((name) => (
                <li
                  key={name}
                  className="inline-flex items-center rounded-full bg-brand-soft px-3 py-1.5 text-[13px] 3xl:text-[14px] text-ink"
                >
                  {name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-[14px] 3xl:text-[15px] text-ink-muted">
              None added yet - Edit Profile is where they go.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProfileOverview;
